import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Phone, Mail, MapPin, Globe, Linkedin } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Vision & Mission', href: '/about#vision-mission' },
    { name: 'Why Choose Us', href: '/services' },
    { name: 'Leadership', href: '/about#leadership' },
    { name: 'Contact', href: '/contact' }
  ];

  const services = [
    'Medical Consumables Distribution',
    'Healthcare Consulting Services',
    'Business Development Solutions',
    'Direct-to-Practitioner Delivery',
    'Operational Efficiency Consulting'
  ];

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/mediquipltd/', name: 'LinkedIn' }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-distro/artifacts/5za3vumk_Mediquip%20Logo%20Original.png"
                  alt="Mediquip Nigeria Limited"
                  className="h-6 w-auto object-contain mr-3 brightness-0 invert"
                />
                <span className="text-white text-sm font-medium">Nigeria Limited</span>
              </div>
              <p className="text-slate-300 leading-relaxed">
                Advancing healthcare delivery in West Africa through premium medical consumables 
                distribution and trusted partnerships.
              </p>
            </div>
            
            <div className="flex items-center space-x-2 text-teal-400">
              <Heart className="w-5 h-5" />
              <span className="text-sm font-medium">Building on Experience, Delivering Excellence</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('/') ? (
                    <Link
                      to={link.href}
                      className="text-slate-300 hover:text-teal-400 transition-colors duration-200 flex items-center group"
                    >
                      <span className="w-1 h-1 bg-teal-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-slate-300 hover:text-teal-400 transition-colors duration-200 flex items-center group"
                    >
                      <span className="w-1 h-1 bg-teal-400 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></span>
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index} className="text-slate-300 text-sm flex items-start">
                  <span className="w-1 h-1 bg-slate-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  {service}
                </li>
              ))}
            </ul>
            
            <div className="mt-8">
              <h4 className="text-md font-semibold text-white mb-4">Key Partnerships</h4>
              <ul className="space-y-2">
                <li className="text-slate-300 text-sm">
                  <span className="text-teal-400">Partner:</span> Merrill Life Sciences
                </li>
                <li className="text-slate-300 text-sm">
                  <span className="text-teal-400">Partner:</span> Wizbiotech
                </li>
                <li className="text-slate-300 text-sm">
                  <span className="text-teal-400">Sub Dealer:</span> Terumo Blood and Cell Technologies
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-teal-400 flex-shrink-0" />
                <a
                  href="tel:+2349063059897"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm"
                >
                  +234 906 305 9897
                </a>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-teal-400 flex-shrink-0" />
                <a
                  href="mailto:mediquipaf@gmail.com"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm"
                >
                  mediquipaf@gmail.com
                </a>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-white mb-3">Follow Us</h4>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-all duration-200"
                      title={social.name}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Mediquip Nigeria Limited. All rights reserved.
            </div>
            
            <div className="flex items-center space-x-6 text-sm">
              <a href="#" className="text-slate-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-400 hover:text-teal-400 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;